import { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

// Components
import PopupTop from 'components/atoms/popup-top/popup-top'

// Types
// Routes
import { currentHome } from 'components/organisms/nav-menu/nav-menu-routes'

// Constants
import storageNames from 'constants/storageNames'

// Services
import AuthService from 'modules/Auth/services/auth'
import AuthManager from 'services/authManager'
import RegisterService from 'modules/Auth/services/register'

// Redux Actions
import {
	activeSubscription,
	deletePatientToUSer,
	setLogOut,
	setPatientToUSer,
	setServiceUser,
	setUserInfo,
	updatePathologyUser,
	updatePatients,
	updateAllPatients
} from 'state/actions/SessionActions'

import {
	removeActivePatient,
	setActivePatient,
	updatePathologyPatient,
	updatePlanPatient
} from 'state/actions/activePatientActions'

// Utils
import { logoutGigya } from 'utils/gigyaControls'

// Hooks
import useAudit from './useAudit'
import useRouterControl from './useRouterControl'
import useUIActions from './useUIActions'
// import useAuthQueries from './useAuthQueries' // Comentado - se usa AuthManager directamente

// Types
import {
	UserInfo,
	RegisterGigyaParams,
	GetUserInfoParams,
	LoginOutParams,
	LoginOutAndCleanParams,
	AuthResponse,
	RegisterResponse,
	EventData,
	RootState,
	UserActionsReturn,
	AuditEvents,
	UIActions
} from 'types/userActions.types'

// Constants
const EVENT_DATA: EventData = {
	registered: 'REGISTERED',
	signup: 'SIGNUP'
} as const

const TIMEOUT_DELAY = 500
const trueValue = 'true'
const falseValue = 'false'

/**
 * Hook personalizado para manejar todas las acciones relacionadas con el usuario
 * @returns {UserActionsReturn} Objeto con todas las funciones y datos del usuario
 */
export default function useUserActions(): UserActionsReturn {
	// Redux selectors
	const {
		session: { userGigya, patients, isLogin, parQ, userId },
		session
	} = useSelector((state: RootState) => state)

	// Local state
	const [sendDatEvent, setSendDatEvent] = useState<string | false>(false)

	// Hooks
	const { handleShowPopupTop }: UIActions = useUIActions()
	const { routerControl } = useRouterControl()
	const { events, handledSaveEventRegistry }: AuditEvents = useAudit()
	const dispatch = useDispatch()

	// Cache de Tanstack Query (mantiene la lógica existente)
	// const { getCachedUserInfo } = useAuthQueries(userId) // Comentado - se usa AuthManager directamente

	// Memoized dispatch functions
	const setInfoUser = useCallback(
		(userInfo: Partial<UserInfo>): void => {
			dispatch(setUserInfo({ ...session, ...userInfo }))
		},
		[dispatch, session]
	)

	const handleLogin = useCallback(
		(userInfo: UserInfo | null): void => {
			if (userInfo == null) return
			dispatch(setUserInfo({ ...userInfo, isLogin: true }))
		},
		[dispatch]
	)

	const handledLoginOut = useCallback((): void => {
		dispatch(setLogOut())
	}, [dispatch])

	const setPathologyUser = useCallback(
		(pathologies: any): void => {
			dispatch(updatePathologyUser({ pathologies }))
		},
		[dispatch]
	)

	const updateInfoUser = useCallback(
		(payload: any): void => {
			dispatch(setUserInfo(payload))
		},
		[dispatch]
	)

	const handledSetServiceUser = useCallback(
		(payload: any): void => {
			dispatch(setServiceUser(payload))
		},
		[dispatch]
	)

	// Patient related functions
	const handleSetAllPatients = useCallback(
		(patients: any[]): void => {
			dispatch(updateAllPatients({ patients }))
		},
		[dispatch]
	)

	const handleActivePatient = useCallback(
		(patient: any): void => {
			dispatch(setActivePatient(patient))
		},
		[dispatch]
	)

	const handleRemoveActivePatient = useCallback((): void => {
		dispatch(removeActivePatient())
	}, [dispatch])

	const setPathologyPatient = useCallback(
		(pathologies: any): void => {
			dispatch(updatePathologyPatient({ pathologies }))
		},
		[dispatch]
	)

	const updateInfoPatient = useCallback(
		(payload: any): void => {
			dispatch(updatePatients(payload))
		},
		[dispatch]
	)

	const activePlanPatient = useCallback(
		(plan: any): void => {
			dispatch(updatePlanPatient({ vitalPlanActivated: plan }))
		},
		[dispatch]
	)

	const setNewPatientToUser = useCallback(
		(patient: any): void => {
			dispatch(setPatientToUSer([...patients, patient]))
		},
		[dispatch, patients]
	)

	const deletePatientToUser = useCallback(
		(patient: any): void => {
			dispatch(deletePatientToUSer(patient))
		},
		[dispatch]
	)

	const setActiveSubscription = useCallback((): void => {
		dispatch(activeSubscription())
	}, [dispatch])

	// Service functions
	/**
	 * Obtiene el token CDC del usuario por email
	 * @param params - Parámetros de la función
	 */
	const getCdCTokenEmailUser = useCallback(
		async ({
			data = false,
			goToHome = false,
			callback = false,
			userClientId = false,
			client = ''
		}: any = {}): Promise<void> => {
			try {
				const shouldProcess = !userClientId || (userClientId && client === 'smartfit')

				if (shouldProcess) {
					localStorage.setItem(storageNames.isUserRegistered, trueValue)
					localStorage.setItem(storageNames.flagUserEmail, trueValue)

					if (data) {
						handleLogin(data)
					}

					const keyValue = data?.token
					if (keyValue) {
						localStorage.setItem(storageNames.authToken, keyValue)
					}

					if (callback) {
						callback()
					}

					if (goToHome) {
						return routerControl({ link: currentHome })
					}
				}

				if (userClientId && client === 'smartfit') {
					localStorage.setItem(storageNames.isUserRegistered, trueValue)
					localStorage.setItem(storageNames.flagUserEmail, trueValue)

					if (data) {
						handleLogin(data)
					}

					const keyValue = data?.token
					if (keyValue) {
						localStorage.setItem(storageNames.authToken, keyValue)
					}

					if (callback) {
						await callback()
					}

					if (goToHome) {
						return routerControl({ link: currentHome })
					}
				}
			} catch (error: any) {
				console.error('Error in getCdCTokenEmailUser:', error)
				handleShowPopupTop(<PopupTop type='error' label={error.message} />)
			}
		},
		[handleLogin, routerControl, handleShowPopupTop]
	)

	/**
	 * Inicia sesión del usuario
	 * @param payload - Datos de login
	 */
	const loginUser = useCallback(
		(payload: any): Promise<AuthResponse> => {
			return AuthService.login({ userId, payload }).then((res: AuthResponse) => {
				if (res.status === 200) {
					localStorage.setItem(storageNames.cognitoToken, JSON.stringify(res.data.tokens))
					localStorage.setItem(storageNames.isUserRegistered, trueValue)
					handleLogin(res.data as UserInfo)
					getUserInfo({})
					getCdCTokenEmailUser({})
				}
				return res
			})
		},
		[userId, handleLogin, getCdCTokenEmailUser]
	)

	/**
	 * Inicia sesión con Gigya
	 */
	const loginGygiaUser = useCallback((): Promise<AuthResponse> => {
		return AuthService.getTokenGigya().then((response: AuthResponse) => {
			if (response.status === 200) {
				localStorage.removeItem(storageNames.gigyaUID)
				localStorage.setItem(storageNames.gigyaToken, response.data.accessToken || '')
			}
			return response
		})
	}, [])

	/**
	 * Registra un usuario de Gigya
	 * @param params - Parámetros de registro
	 */
	const handledRegisterGigyaUser = useCallback(
		({ data, token, pauseLoading, email }: RegisterGigyaParams): void => {
			const { documentType = null, identityCard = null } = data || {}

			const basicPayload = {
				role: 'PACIENTE',
				userGigya: true,
				userWithoutLogin: false,
				email
			}

			// Payload para actualizar usuario CDC y finalizar proceso
			const payload: UserInfo | null =
				identityCard && documentType
					? {
							...session,
							...data,
							...basicPayload,
							registrationFinished: true
					  }
					: null

			if (!payload) {
				handleShowPopupTop(<PopupTop type='error' label='Datos de registro incompletos' />)
				return
			}

			RegisterService.registerUser(payload)
				.then(async (res: RegisterResponse) => {
					localStorage.removeItem(storageNames.authToken)
					localStorage.removeItem(storageNames.gigyaUID)

					if (res.status === 200) {
						localStorage.setItem(storageNames.gigyaToken, token)
						localStorage.setItem(storageNames.isUserRegistered, trueValue)

						const dataLogin: UserInfo = {
							...res.data,
							userWithoutLogin: false,
							userGigya: true
						}

						handleLogin(dataLogin)

						if (parQ) {
							// Flag para verificar si un usuario de smartfit stream está registrado
							localStorage.setItem('registerUserSmartFit', trueValue)

							// Mostrar tooltip en el botón mi perfil en nav
							const nameList = storageNames.tooltipList
							const dataNameList = localStorage.getItem(nameList)
							if (dataNameList) {
								localStorage.setItem(nameList, dataNameList.replace(',/perfil', ''))
							}
						}

						setSendDatEvent(EVENT_DATA.signup)
						return res
					}

					if (pauseLoading) {
						pauseLoading()
					}

					handleShowPopupTop(
						<PopupTop type='error' label={res.message || 'Error en el registro'} />
					)
				})
				.then((res: RegisterResponse | undefined) => {
					if (res?.status === 200) {
						getUserInfo({ CDCFlow: true })

						// Mostrar texto de registro exitoso
						handleShowPopupTop(
							<PopupTop type='success' label='Has realizado tu suscripción con éxito' />
						)
						return routerControl({ link: currentHome })
					}
				})
				.catch((error: Error) => {
					localStorage.removeItem(storageNames.authToken)
					localStorage.removeItem(storageNames.gigyaUID)
					localStorage.removeItem(storageNames.gigyaToken)
					localStorage.setItem(storageNames.isUserRegistered, falseValue)

					if (pauseLoading) {
						pauseLoading()
					}

					handleShowPopupTop(<PopupTop type='error' label={error.message} />)
				})
		},
		[session, parQ, handleLogin, routerControl, handleShowPopupTop]
	)

	/**
	 * Obtiene la información del usuario
	 * @param params - Parámetros de la función
	 */
	const getUserInfo = useCallback(
		({ callback = false }: GetUserInfoParams = {}): void => {
			if (AuthService.isLoggedIn(true) && isLogin) {
				// Usar AuthManager para evitar duplicados, pero mantener cache
				AuthManager.getUserInfo(userId)
					.then((res: AuthResponse) => {
						if (res.status === 200) {
							// Temporalmente comentado por error con usuarios logueados de smartfit
							// res.data.userId === userId && setInfoUser(res.data)
							setInfoUser(res.data as UserInfo)

							if (callback) {
								callback()
							}
						}
					})
					.catch((err: Error) => {
						console.error('Error getting user info:', err)
						handleShowPopupTop(<PopupTop type='error' label={err.message} />)
					})
			}
		},
		[isLogin, userId, setInfoUser, handleShowPopupTop]
	)

	/**
	 * Cierra sesión del usuario
	 * @param params - Parámetros de logout
	 */
	const loginOut = useCallback(
		async ({ noRedirect = false }: LoginOutParams = {}): Promise<any> => {
			try {
				handleRemoveActivePatient()

				if (userGigya) {
					await logoutGigya()
				}

				// Limpiar estado del AuthManager
				AuthManager.clearState()

				handledLoginOut()
				return AuthService.logout({ noRedirect })
			} catch (error: any) {
				console.error('Error in loginOut:', error)
				handleShowPopupTop(<PopupTop type='error' label='Error al cerrar sesión' />)
			}
		},
		[handleRemoveActivePatient, userGigya, handledLoginOut, handleShowPopupTop]
	)

	/**
	 * Cierra sesión y limpia datos
	 * @param params - Parámetros de logout y limpieza
	 */
	const loginOutAndClean = useCallback(
		async ({ migrate = false }: LoginOutAndCleanParams = {}): Promise<any> => {
			try {
				if (migrate) {
					localStorage.setItem('MIGRATE', trueValue)
				}

				handleRemoveActivePatient()

				if (userGigya) {
					await logoutGigya()
				}

				return AuthService.logout({ noRedirect: false })
			} catch (error: any) {
				console.error('Error in loginOutAndClean:', error)
				handleShowPopupTop(<PopupTop type='error' label='Error al cerrar sesión' />)
			}
		},
		[handleRemoveActivePatient, userGigya, handleShowPopupTop]
	)

	const refreshUserInfo = () => {
		AuthService.getUserInfo({ userId }).then((response: any) => {
			if (response.status === 200) {
				setInfoUser(response?.data?.data as any)
			}
		})
	}

	// Effect para manejar eventos de datos
	useEffect(() => {
		if (!sendDatEvent) return

		const setEventName = ({ send, data }: { send: string; data: string }): string => {
			return send === data ? events[3] : events[1]
		}

		const sendDataToEvent = setTimeout(() => {
			handledSaveEventRegistry({
				eventName: setEventName({
					send: sendDatEvent,
					data: EVENT_DATA.registered
				}),
				noEmail: true
			})
		}, TIMEOUT_DELAY)

		return () => clearTimeout(sendDataToEvent)
	}, [sendDatEvent, events, handledSaveEventRegistry])

	// Memoized return object
	return useMemo(
		(): UserActionsReturn => ({
			// User actions
			handledRegisterGigyaUser,
			setInfoUser,
			handleLogin,
			loginUser,
			loginOutAndClean,
			loginOut,
			setPathologyUser,
			updateInfoUser,
			setPathologyPatient,
			getUserInfo,
			refreshUserInfo,
			handledLoginOut,
			deletePatientToUser,
			setActiveSubscription,
			handledSetServiceUser,

			// Patient actions
			handleRemoveActivePatient,
			handleActivePatient,
			handleSetAllPatients,
			updateInfoPatient,
			activePlanPatient,
			setNewPatientToUser,

			// Authentication actions
			loginGygiaUser,
			getCdCTokenEmailUser,
			setSendDatEvent,

			// Data
			sendDatEvent,
			eventData: EVENT_DATA
		}),
		[
			// User actions dependencies
			handledRegisterGigyaUser,
			setInfoUser,
			handleLogin,
			loginUser,
			loginOutAndClean,
			loginOut,
			setPathologyUser,
			updateInfoUser,
			setPathologyPatient,
			getUserInfo,
			refreshUserInfo,
			handledLoginOut,
			deletePatientToUser,
			setActiveSubscription,
			handledSetServiceUser,

			// Patient actions dependencies
			handleRemoveActivePatient,
			handleActivePatient,
			handleSetAllPatients,
			updateInfoPatient,
			activePlanPatient,
			setNewPatientToUser,

			// Authentication actions dependencies
			loginGygiaUser,
			getCdCTokenEmailUser,
			setSendDatEvent,

			// Data dependencies
			sendDatEvent
		]
	)
}
